import React from 'react';

/**
 * What actually happened on the last submit, per group.
 *
 * Exists because both encoding screens used to report a single uniform result:
 * VAT showed one toast for the whole batch, and Percentage Tax — which posts
 * once per quarter — counted LOCAL rows, so a quarter the server silently
 * dropped still read as "saved successfully". A partial result must never look
 * like a clean one, so each group reports its own outcome and rows that are not
 * confirmed say so in the words that matter: they are still in the grid, and
 * they are still in the local draft.
 *
 * Statuses:
 *   saved   — server confirmed every row in the group; rows cleared
 *   partial — server accepted some rows and dropped others; only the rows the
 *             echo confirms are cleared, the rest stay
 *   failed  — server rejected the request; nothing saved, rows kept
 *   unknown — no response at all (timeout/offline); may or may not have saved
 */

const TONE = {
  saved: {
    wrap: 'bg-success-50 border-success-200',
    label: 'text-success-700',
    icon: '✓',
  },
  partial: {
    wrap: 'bg-warning-50 border-warning-200',
    label: 'text-warning-700',
    icon: '!',
  },
  failed: {
    wrap: 'bg-danger-50 border-danger-200',
    label: 'text-danger-700',
    icon: '×',
  },
  unknown: {
    wrap: 'bg-warning-50 border-warning-200',
    label: 'text-warning-700',
    icon: '?',
  },
};

const STATUS_TEXT = {
  saved: 'Saved',
  partial: 'Partly saved',
  failed: 'Not saved',
  unknown: 'Unconfirmed',
};

const SaveOutcomePanel = ({ outcomes, onDismiss }) => {
  if (!outcomes || outcomes.length === 0) return null;

  const unresolved = outcomes.filter((o) => o.status !== 'saved');
  const needsCheck = outcomes.some((o) => o.status === 'partial' || o.status === 'unknown');

  return (
    <div className="border border-ink-line rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-ink-canvas border-b border-ink-line">
        <span className="text-sm font-medium text-ink">Last save</span>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="text-xs text-ink-body hover:text-ink rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40"
          >
            Dismiss
          </button>
        )}
      </div>

      <ul className="divide-y divide-ink-line">
        {outcomes.map((outcome) => {
          const tone = TONE[outcome.status] || TONE.failed;
          return (
            <li key={outcome.id} className={`flex gap-3 px-3 py-2 border-l-4 ${tone.wrap}`}>
              <span className={`text-sm font-semibold ${tone.label}`} aria-hidden="true">
                {tone.icon}
              </span>
              <div className="text-sm">
                <p className={`font-medium ${tone.label}`}>
                  {outcome.label} — {STATUS_TEXT[outcome.status] || 'Not saved'}
                  {typeof outcome.savedCount === 'number' && typeof outcome.sentCount === 'number' && (
                    <span className="font-normal text-ink-body">
                      {' '}
                      ({outcome.savedCount} of {outcome.sentCount}{' '}
                      {outcome.sentCount === 1 ? 'row' : 'rows'})
                    </span>
                  )}
                </p>
                {outcome.message && <p className="text-ink-body mt-0.5">{outcome.message}</p>}
              </div>
            </li>
          );
        })}
      </ul>

      {unresolved.length > 0 && (
        <p className="px-3 py-2 text-xs text-ink-body bg-ink-canvas border-t border-ink-line">
          Rows that were not confirmed are still in the grid and in this browser&apos;s autosave —
          nothing typed has been lost.
          {needsCheck && (
            <>
              {' '}
              Check the records list before resubmitting an unconfirmed row: it may already exist.
            </>
          )}
        </p>
      )}
    </div>
  );
};

export default SaveOutcomePanel;
